import React, { useRef, useId } from "react";
import * as c from "./homepageVideoCarousel.module.scss";
import useWistiaPlayer from "@hooks/useWistiaPlayer";
import classNames from "classnames";
import { gsap } from "gsap";
import { useGSAP } from "@gsap/react";
import { Draggable } from "gsap/Draggable";
import { Script } from "gatsby";
import Monica from "@images/homepage/video-carousel/1.jpg";
import Paola from "@images/homepage/video-carousel/2.jpg";
import Gina from "@images/homepage/video-carousel/3.jpg";
import John from "@images/homepage/video-carousel/4.jpg";
import Angela from "@images/homepage/video-carousel/5.jpg";
import Brett from "@images/homepage/video-carousel/6.jpg";
import Arielle from "@images/homepage/video-carousel/7.jpg";

const HomepageVideoCarousel = () => {
  const testimonials = [
    { name: "Monica DeAngelis", company: "Bare Laser Medspa", videoId: "38zaosrc0v", img: Monica },
    { name: "Paola Girotti", company: "Sugarmoon", videoId: "0zdjnw0a1b", img: Paola },
    { name: "Gina Monique Lafler", company: "My Little Beautique", videoId: "pnd5na2t77", img: Gina },
    { name: "John Cohn", company: "Venice Soleil", videoId: "dwgsmw053g", img: John },
    { name: "Angela Walker", company: "N Natural Hair Studio", videoId: "wimjhja0bt", img: Angela },
    { name: "Brett Foreman", company: "Pony Studios Co", videoId: "m6un2lg8sf", img: Brett },
    { name: "Arielle Deguzman", company: "House of Aanuko", videoId: "t10kmj8pfg", img: Arielle },
  ];

  const VideoImage = ({ img, alt, name, company, videoId, index }) => {
    const wistiaContainerId = useId();
    useWistiaPlayer({
      wistiaContainerId: wistiaContainerId,
      trackedEventName: `Video Carousel - ${name} - ${company}`,
    });
    return (
      <div data-image-scroll className={classNames(c.imageWrap, "testimonial-image")} style={{ "--index": index }}>
        <div
          id={wistiaContainerId}
          className={classNames(
            c.wistiaVideo,
            "wistia_embed",
            `wistia_async_${videoId}`,
            "seo=false",
            "videoFoam=true",
            "popover=true",
            "popoverContent=link"
          )}
        ></div>
        <div className={c.imgContainer}>
          <img src={img} alt={alt} className={c.image} />
          <svg viewBox="0 0 23 23" className={c.play}>
            <circle fill="#bc269b" stroke="#bc269b" strokeWidth={2} cx="11.5" cy="11.5" r="10.5" />
            <path
              fill="#fcfafa"
              stroke="#bc269b"
              strokeWidth="1"
              d="M8.5,8.3 C8.5,7.74771525 8.94771525,7.3 9.5,7.3 C9.68741972,7.3 9.87106734,7.35266944 10.0299989,7.4520017 L15.1432027,10.6477541 C15.6115392,10.9404644 15.7539124,11.5574148 15.4612021,12.0257513 C15.3807214,12.1545204 15.2719718,12.26327 15.1432027,12.3437507 L10.0299989,15.539503 L10.0299989,15.539503 C9.87106734,15.6388353 9.68741972,15.6915047 9.5,15.6915047 C8.98716416,15.6915047 8.56449284,15.3054645 8.50672773,14.8081258 L8.5,14.6915047 L8.5,8.3 Z"
            />
          </svg>
        </div>

        {name && <h3 className={classNames(c.name, "p2")}>{name}</h3>}
        {company && <p className={classNames(c.company, "eyebrow2")}>{company}</p>}
      </div>
    );
  };

  const container = useRef();
  const carouselContainer = useRef();
  gsap.registerPlugin(Draggable);

  useGSAP(
    () => {
      let mm = gsap.matchMedia();
      let wrapperWidth = 1100;
      let windowWidth = Math.max(
        (window.innerWidth - wrapperWidth) / 2,
        Math.min(window.innerWidth - wrapperWidth, 400)
      );
      function getBgWidth() {
        wrapperWidth = 1100;
        windowWidth = Math.max((window.innerWidth - wrapperWidth) / 2, Math.min(window.innerWidth - wrapperWidth, 400));
      }
      mm.add("(min-width: 1100px)", () => {
        // Only apply the carousel transition on larger screens

        window.addEventListener("resize", getBgWidth);
        gsap.set(carouselContainer.current, { "--clipPath": windowWidth + "px" });
        gsap
          .timeline({
            paused: false,
            scrollTrigger: {
              trigger: carouselContainer.current,
              start: () => "top 100%+=100px",
              end: () => `bottom 0%+=50px`,
              invalidateOnRefresh: false,
              scrub: 1,
            },
          })
          .to(carouselContainer.current, { "--clipPath": "0px", duration: 50 })
          .to(carouselContainer.current, { "--clipPath": windowWidth / 2 + "px", duration: 25 });
      });

      return () => {
        window.removeEventListener("resize", getBgWidth);
      };
    },
    { scope: container, dependencies: [carouselContainer.current] }
  );

  const slider = useRef();
  const proxyRef = useRef();

  useGSAP(
    () => {
      const marquee = slider.current;
      const proxy = proxyRef.current;
      const allItems = slider.current.querySelectorAll("[data-customer-row]");
      const direction = "to-left";
      const directionVal = direction === "to-left" ? -1 : 1;

      let totalX;
      let itemW;
      let animation;
      let currentIsMobile = window.innerWidth <= 1100;

      const checkMobile = () => {
        const newIsMobile = window.innerWidth <= 1100;
        if (newIsMobile !== currentIsMobile) {
          currentIsMobile = newIsMobile;
          // Trigger setup with new mobile state immediately
          requestAnimationFrame(() => {
            setUp();
            updateAnimation();
          });
        }
      };

      const setUp = () => {
        totalX = 0;
        // Use current window width check instead of state
        const mobile = window.innerWidth <= 1100;
        itemW = mobile ? 265 * testimonials.length : 295 * testimonials.length;

        allItems.forEach((item) => {
          gsap.set(item, {
            x: totalX,
            width: itemW,
            // Ensure proper spacing between items
            marginRight: mobile ? "20px" : "30px",
          });
          totalX += itemW;
        });
      };

      const createAnimation = () => {
        const stringX = directionVal === -1 ? `-=${totalX}` : `+=${totalX}`;

        if (animation) {
          animation.kill();
        }

        animation = gsap.to("[data-customer-row]", {
          repeat: -1,
          duration: 9 * testimonials.length,
          x: stringX,
          ease: "none",
          paused: true,
          modifiers: {
            x: function (x, target) {
              if (directionVal === -1) {
                x = ((parseInt(x) - totalX) % totalX) + (totalX - itemW);
              } else {
                x = parseInt(x) % totalX;
              }
              target.style.visibility = x > marquee.offsetWidth ? "hidden" : "visible";
              return `${x}px`;
            },
          },
        });

        return animation;
      };

      const updateAnimation = () => {
        const wasPlaying = animation ? !animation.paused() : true;
        const currentProgress = animation ? animation.progress() : 0;

        createAnimation();

        // Apply the preserved progress to the new animation
        const marqueeProgress = gsap.utils.wrap(0, 1);
        animation.progress(marqueeProgress(currentProgress));

        // Update proxy and marquee dimensions
        gsap.set(proxy, { width: totalX });
        gsap.set(marquee, { width: totalX });
        proxy.style.width = `${totalX}px`;

        // Force a render to apply changes
        animation.render(animation.time(), false, true);

        // Restart animation if it was playing before resize
        if (wasPlaying) {
          animation.play();
        }
      };

      // Initial setup
      setUp();
      createAnimation();

      // Set initial marquee position
      gsap.set([marquee], { xPercent: 0 });

      // Mouse event handlers
      const handleMouseEnter = () => {
        animation.pause();
      };

      const handleMouseLeave = () => {
        // Use current window width check instead of state
        const mobile = window.innerWidth <= 1100;
        !mobile && animation.play();
      };

      marquee.addEventListener("mouseenter", handleMouseEnter);
      marquee.addEventListener("mouseleave", handleMouseLeave);

      // Progress update function for dragging
      function updateProgress() {
        const marqueeVal = gsap.utils.wrap(0, totalX);
        const marqueeProgress = gsap.utils.wrap(0, 1);
        const dragValue = marqueeVal(this.deltaX * directionVal) / totalX;
        const currentProgressAnim = animation.progress();
        const endProgress = marqueeProgress(currentProgressAnim + dragValue);
        animation.progress(endProgress);
      }

      // Create draggable
      const draggableInstance = Draggable.create(proxy, {
        type: "x",
        trigger: slider.current,
        inertia: true,
        onDrag: updateProgress,
        onThrowUpdate: updateProgress,
      })[0];

      // Improved resize handler with debouncing
      let resizeTimeout;
      const handleResize = () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          const wasMouseEntered = marquee.matches(":hover");
          checkMobile();

          // Only restart animation if mouse is not hovering and on desktop
          if (!wasMouseEntered && window.innerWidth > 1100) {
            // Small delay to ensure animation restarts smoothly
            setTimeout(() => {
              if (animation && animation.paused()) {
                animation.play();
              }
            }, 100);
          }
        }, 16); // ~60fps debouncing
      };

      // Add resize listeners
      window.addEventListener("resize", handleResize);

      // Start the animation
      animation.play();

      // Cleanup function
      return () => {
        clearTimeout(resizeTimeout);
        window.removeEventListener("resize", handleResize);
        marquee.removeEventListener("mouseenter", handleMouseEnter);
        marquee.removeEventListener("mouseleave", handleMouseLeave);

        if (animation) {
          animation.kill();
        }

        if (draggableInstance) {
          draggableInstance.kill();
        }
      };
    },
    {
      scope: container,
      revertOnUpdate: true,
      dependencies: [slider.current, proxyRef.current, carouselContainer.current, testimonials.length],
    }
  );

  return (
    <section className={c.container} ref={container}>
      <div ref={proxyRef} style={{ display: "none" }}></div>
      <Script src={"https://fast.wistia.com/assets/external/E-v1.js"} async strategy="idle" />
      <div
        className={classNames(c.marquee)}
        ref={carouselContainer}
        style={{ "--customerCount": testimonials.length / 2 }}
      >
        <p className={classNames(c.label, "eyebrow")}>Customer Stories</p>
        <h2 className={"h2"}>Loved by thousands of salons and spas</h2>
        <div ref={slider} className={c.customerWrap}>
          <div id="track" className={classNames("top-images", c.customerrow)}>
            <div data-customer-row className={classNames(c.innerrow, "customer-row")}>
              {testimonials.map((testimonial, index) => (
                <VideoImage
                  key={`${testimonial.name}`}
                  img={testimonial.img}
                  index={index}
                  alt={testimonial.name}
                  name={testimonial.name}
                  company={testimonial.company}
                  videoId={testimonial.videoId}
                />
              ))}
            </div>
            <div data-customer-row className={classNames(c.innerrow, "customer-row")}>
              {testimonials.map((testimonial, index) => (
                <VideoImage
                  key={`${testimonial.name}-${index}`}
                  img={testimonial.img}
                  index={index}
                  alt={testimonial.name}
                  name={testimonial.name}
                  company={testimonial.company}
                  videoId={testimonial.videoId}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export { HomepageVideoCarousel };
